import { verticeToothMaterial } from "../Provider/MaterialProvider";
import { PhysicsWorld, Rapier } from "../Provider/RapierProvider";
import { LineBasicMaterial, Line, BufferGeometry, Group, Quaternion, Vector3, BufferAttribute, Color, Box3, PointsMaterial, Points } from 'three';
import { labelBuilder } from "../Builder/LabelBuilder";
import { synchronizeColliderTransformation } from "../Builder/PhysicsWorldBuilder";
import { Store } from "Application/Store/Store";
import { meshSelectionStore } from 'Domain/Mesh/State/MeshSelectionStore';
import { threeEngine } from 'Domain/Scene/Engine/ThreeEngine';
import { getViewportMode } from "./ViewportHandler";
import { delay } from '../Manager/InitializationOrchestrator';

const tempGroup = new Group();
threeEngine.setTempGroup(tempGroup);
export let lastContacts = [];
let boundingBoxes = [];

export const colorStops = [
    { maxT: 0, color: new Color(190 / 255, 178 / 255, 154 / 255) },
    { maxT: 0.2, color: new Color(1, 1, 1) },
    { maxT: 0.4, color: new Color(142 / 255, 180 / 255, 227 / 255) },
    { maxT: 0.5, color: new Color(31 / 255, 73 / 255, 125 / 255) },
    { maxT: Infinity, color: new Color(0 / 255, 32 / 255, 96 / 255) }
];

class Contact {
    constructor(body) {
        this.body = body;
        this.maxDistanceMeshPoint = null;
        this.maxDistanceValue = 0;
        this.meshPoints = [];
    }
}

/**
 * Classe représentant une boîte englobante calculée à partir de points.
 * @param {Array} points - Liste des points pour construire la boîte.
 * @param {Object} contactA - Premier objet de contact associé à la boîte.
 * @param {Object} contactB - Deuxième objet de contact associé à la boîte.
 * @param {number} [margin=0] - Marge à appliquer à la boîte.
 * 
 * @property {Object} contactA - Premier objet de contact associé à la boîte.
 * @property {Object} contactB - Deuxième objet de contact associé à la boîte.
 * @property {number} maxDistanceValue - La valeur maximale de distance entre les deux contacts.
 * @property {Object} box - La boîte englobante.
 */
class BoundingBox {
    constructor(points, contactA, contactB, margin = 0) {
        this.contactA = new Contact(contactA);
        this.contactB = new Contact(contactB);
        this.maxDistanceValue = -Infinity;
        this.margin = margin;
        this.box = new Box3().setFromPoints(points);

        if (margin !== 0) {
            this.box.expandByScalar(margin);
        }

        this.computeMeshPoints();
    }

    /**
     * Définit les points du maillage qui se trouvent dans la boîte englobante. 
     */
    computeMeshPoints() {
        [this.contactA, this.contactB].forEach(contact => {
            getPointsFromTooth(contact.body.teethRef).forEach(point => {
                if (this.box.containsPoint(point.worldPoint)) {
                    contact.meshPoints.push(point);
                }
            });
        });
    }

    /**
     * Calcule les distances des points du maillage par rapport aux deux contacts.
     */
    computeMeshPointsDistances() {
        this.contactA.meshPoints.forEach(meshPoint => {
            meshPoint.computeDistance(this.contactB.body);
        });
        this.contactB.meshPoints.forEach(meshPoint => {
            meshPoint.computeDistance(this.contactA.body);
        });
    }

    /**
     * Calcule les faux positifs des points du maillage par rapport aux deux contacts. Multiplie la distance par -1 si le point n'est pas à l'intérieur.
     */
    async computeFalsePositiveByRaycast() {
        const rapier = await Rapier();
        const physicsWorld = await PhysicsWorld();

        const rayDirections = [
            { x: 1, y: 0, z: 0 },
            { x: -1, y: 0, z: 0 },
            { x: 0, y: 1, z: 0 },
            { x: 0, y: -1, z: 0 },
            { x: 0, y: 0, z: 1 },
            { x: 0, y: 0, z: -1 }
        ];

        const processPoints = async (sourcePoints, targetCollider) => {
            for (const meshPoint of sourcePoints) {
                let insideCount = 0;
                const origin = meshPoint.worldPoint;

                for (const dir of rayDirections) {
                    const ray = new rapier.Ray(
                        { x: origin.x, y: origin.y, z: origin.z },
                        dir
                    );

                    let intersectionCount = 0;
                    physicsWorld.intersectionsWithRay(
                        ray,
                        5000, // maxToi
                        false, // solid
                        (hit) => {
                            if (hit.collider === targetCollider) {
                                intersectionCount++;
                            }
                            return true;
                        }
                    );

                    // si le nombre d'intersections est impair, le point est à l'intérieur
                    if (intersectionCount % 2 === 1) {
                        insideCount++;
                    }
                }

                if (insideCount < rayDirections.length * 1) {
                    meshPoint.distance *= -1;
                }
            }
        };

        // Process points for both contacts
        await processPoints(this.contactA.meshPoints, this.contactB.body);
        await processPoints(this.contactB.meshPoints, this.contactA.body);
    }

    /**
     * Calcul la distance maximale entre les deux contacts et le point de contact le plus éloigné.
     */
    computeContactStat() {

        [this.contactA, this.contactB].forEach(contact => {
            contact.meshPoints.forEach(meshPoint => {
                if (meshPoint.distance > contact.maxDistanceValue) {
                    contact.maxDistanceMeshPoint = meshPoint;
                    contact.maxDistanceValue = meshPoint.distance;
                }
            });
        });

        this.maxDistanceValue = ((this.contactA.maxDistanceValue + this.contactB.maxDistanceValue) / 2);
    }

    /**
     * Affiche le stripping entre les deux contacts.
     */
    displayStripping() {

        this.computeContactStat();

        if (this.maxDistanceValue < 0.1) return;
        if (!this.contactA.maxDistanceMeshPoint || !this.contactB.maxDistanceMeshPoint) return;

        const contactCenter = new Vector3();
        contactCenter.add(this.contactA.maxDistanceMeshPoint.worldPoint);
        contactCenter.add(this.contactB.maxDistanceMeshPoint.worldPoint);
        contactCenter.divideScalar(2);

        const translationVectorA = this.contactA.body.teethRef.vecArchTemplateNormal.clone().multiplyScalar(-10);
        const translationVectorB = this.contactB.body.teethRef.vecArchTemplateNormal.clone().multiplyScalar(-10);
        const averageTranslationVector = translationVectorA.add(translationVectorB).divideScalar(2);

        const translatedCenter = contactCenter.clone().add(averageTranslationVector);
        const AvgValue = this.maxDistanceValue.toFixed(2);

        const jawSide = this.contactA.body.teethRef.jawSide;
        if (jawSide === 1) translatedCenter.z = 6;
        else translatedCenter.z = 22;

        let hexBackgroundColor;
        if (AvgValue < 1) {
            const backgroundColor = getFixedColor(this.maxDistanceValue);
            hexBackgroundColor = '#' + backgroundColor.getHexString();
        }
        else hexBackgroundColor = "#f60303";
      
        const label = labelBuilder(AvgValue, 1.5, translatedCenter, hexBackgroundColor, tempGroup);
        const material = new LineBasicMaterial({ color: 0x00FFFF });
        const geometry = new BufferGeometry().setFromPoints([contactCenter, translatedCenter]);
        const line = new Line(geometry, material);
        tempGroup.add(line);

        line.layers.set(jawSide);
        label.layers.set(jawSide);
    }
}

/**
 * Classe représentant un point sur un maillage 3D avec son indice et sa position dans l'espace.
 * @param {number} index - L'indice du point dans la géométrie du maillage.
 * @param {Object} worldPoint - Le point dans l'espace mondial.
 * 
 * @property {number} index - L'indice du point dans la géométrie du maillage.
 * @property {Object} worldPoint - Le point dans l'espace mondial.
 * @property {number} distance - La distance projetée du point.
 */
class MeshPoint {
    constructor(index, worldPoint) {
        this.index = index;
        this.worldPoint = worldPoint.clone();
        this.distance = 0;
    }

    computeDistance(contact) {
        const projection = contact.projectPoint(this.worldPoint);

        this.distance = this.worldPoint.distanceTo(
            new Vector3(projection.point.x, projection.point.y, projection.point.z)
        );

        return this.distance;
    }

    addToScene() {

        const material = new PointsMaterial({ color: 0x00FF00, size: 0.4 });
        const geometry = new BufferGeometry().setFromPoints([this.worldPoint]);
        const point = new Points(geometry, material);

        point.matrixAutoUpdate = false;
        point.matrix.identity();

        tempGroup.add(point);

    }
}


class filterIntersectionPointsByOneContact {
    constructor(contact) {
        this.contact = contact;
    }

    filter(contactA, contactB) {
        return contactA.handle === this.contact.handle || contactB.handle === this.contact.handle;
    }
}

/** Fonction qui récupère tous les points de collisions entre tous les collider */
const getAllIntersectionPoints = async (filter = null) => {
    const rapier = await Rapier();
    const physicsWorld = await PhysicsWorld();

    const allToothObjects = threeEngine.getAllToothObjects();
    if (!allToothObjects) return;
    await Promise.all(allToothObjects.map(async tooth => {
        synchronizeColliderTransformation(tooth.body, tooth.refObj);
    }));

    const intersectionPoints = [];
    const eventQueue = new rapier.EventQueue(true);
    const processedPairs = new Set();

    await physicsWorld.step(eventQueue);

    physicsWorld.forEachCollider((colliderA) => {
        const positionA = colliderA.translation();
        const rotationA = colliderA.rotation();

        physicsWorld.forEachCollider((colliderB) => {

            if (colliderA === colliderB) return;
            if (filter && !filter.filter(colliderA, colliderB)) return;

            const pairKey = `${colliderA.handle}-${colliderB.handle}`;
            const reversePairKey = `${colliderB.handle}-${colliderA.handle}`;

            if (processedPairs.has(reversePairKey)) return;
            processedPairs.add(pairKey);

            const points = [];
            physicsWorld.contactPair(colliderA, colliderB, (manifold, flipped) => {
                for (let i = 0; i < manifold.numContacts(); i++) {
                    const localPoint = flipped
                        ? manifold.localContactPoint2(i)
                        : manifold.localContactPoint1(i);

                    const rotatedPoint = applyRotation(localPoint, rotationA);
                    const worldPoint = new Vector3(
                        positionA.x + rotatedPoint.x,
                        positionA.y + rotatedPoint.y,
                        positionA.z + rotatedPoint.z
                    );

                    points.push(worldPoint);
                }
            });

            if (points.length > 0) {
                intersectionPoints.push({
                    contactA: colliderA,
                    contactB: colliderB,
                    points: points
                });
            }
        });
    });

    return intersectionPoints;
};

/** Fonction qui récupères les bounding box de tous les points d'intersection */
const getAllBoundingBoxes = (intersectionPoints) => {
    return intersectionPoints.map(({ contactA, contactB, points }) => {
        return new BoundingBox(points, contactA, contactB, 0.5);
    });
};

/**
 * Récupère tous les points d'un maillage 3D.
 * @param {Object} mesh - Le maillage à analyser.
 * @returns {Array} - Liste des points transformés du maillage.
 */
// Réutiliser un Vector3 temporaire pour éviter les allocations
const _tempLocalPoint = new Vector3();
const _tempWorldPoint = new Vector3();

const getPointsFromTooth = (tooth) => {

    const mesh = tooth.refObj;
    const geometry = mesh.geometry;
    const positions = geometry.attributes.position.array;
    const meshPoints = [];
    const matrixWorld = mesh.matrixWorld;

    for (let i = 0; i < positions.length; i += 3) {
        // Réutiliser le Vector3 temporaire
        _tempLocalPoint.set(positions[i], positions[i + 1], positions[i + 2]);
        _tempWorldPoint.copy(_tempLocalPoint).applyMatrix4(matrixWorld);
        
        // Créer un nouveau Vector3 pour MeshPoint (nécessaire car stocké)
        const worldPoint = _tempWorldPoint.clone();
        const meshPoint = new MeshPoint(i / 3, worldPoint);
        meshPoints.push(meshPoint);
    }

    return meshPoints;
};

/**
 * Récupère la couleur associée à une valeur de distance donnée selon des paliers de couleur.
 * @param {number} t - Valeur de la distance normalisée.
 * @returns {Object} - Couleur associée à la valeur donnée.
 */
const getFixedColor = (t) => {
    if (t <= 0) return colorStops[0].color;
    return colorStops.find(({ maxT }) => t <= maxT).color;
};

/**
 * Applique une coloration aux sommets d'un maillage en fonction des distances des points.
 * @param {Object} tooth - La dent à colorer.
 */
const colorizeToothMeshVertices = (tooth) => {
    if (!boundingBoxes || boundingBoxes.length === 0) return;

    const maxDistance = 0.5;
    const geometry = tooth.refObj.geometry;
    const count = geometry.attributes.position.count;

    // Initialiser le tableau de couleurs avec la couleur de base
    const toothColor = tooth.refObj.material.color;
    const toothColorArray = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
        toothColorArray[i * 3] = toothColor.r;
        toothColorArray[i * 3 + 1] = toothColor.g;
        toothColorArray[i * 3 + 2] = toothColor.b;
    }
    geometry.setAttribute('color', new BufferAttribute(toothColorArray, 3));

    const colors = geometry.attributes.color.array;
    const maxDistances = new Map();

    // Parcourir toutes les boundingBoxes pour trouver les points concernés
    boundingBoxes.forEach(box => {
        // Vérifier si la dent est impliquée dans cette boîte
        const contact = box.contactA.body.teethRef === tooth ? box.contactA :
            box.contactB.body.teethRef === tooth ? box.contactB : null;

        if (contact) {
            contact.meshPoints.forEach(point => {
                if (point.distance > -0.3) {
                    if (!maxDistances.has(point.index) || point.distance > maxDistances.get(point.index)) {
                        maxDistances.set(point.index, point.distance);
                    }
                }
            });
        }
    });

    // Appliquer les couleurs
    maxDistances.forEach((distance, index) => {

        if (distance <= -0.3) return;

        const baseIndex = index * 3;
        const t = Math.min(distance / maxDistance, 1);
        const color = getFixedColor(t);

        colors[baseIndex] = color.r;
        colors[baseIndex + 1] = color.g;
        colors[baseIndex + 2] = color.b;
    });

    geometry.attributes.color.needsUpdate = true;

    tooth.refObj.material = verticeToothMaterial.clone();
    tooth.refObj.material.vertexColors = true;
    tooth.refObj.material.needsUpdate = true;
    tooth.refObj.material.proceced = true;
};


/**
    * Met à jour les bounding boxes pour une dent sélectionnée.
    * @param {Object} selectedTooth - La dent sélectionnée.
    */
export const updateToothIntersection = async (selectedTooth) => {

    if (!boundingBoxes || boundingBoxes.length === 0) {
        await computeOclusionsMap();
    }
    else {
        // supprimer les bounding boxes lié à la dent sélectionnée
        boundingBoxes = boundingBoxes.filter(box => {
            if (box.contactA.body.teethRef === selectedTooth || box.contactB.body.teethRef === selectedTooth) {
                return false;
            }
            return true;
        });

        // recalculer les nouvelles boundings box pour la dent sélectionnée
        const filter = new filterIntersectionPointsByOneContact(selectedTooth.body);
        await computeOclusionsMap(filter);
    }
};

/**
 * Met à jour les bounding boxes pour toutes les dents.
 */
export const computeOclusionsMap = async (filter = null) => {

    if (filter === null) {
        boundingBoxes = [];  // dans le doute pour éviter tout problème on vide la liste des bounding boxes
        // Delai intentionnel pour laisser le temps au garbage collector de liberer la memoire
        await delay(100);
    }

    const intersectionPoints = await getAllIntersectionPoints(filter);
    const newBoundingBoxes = getAllBoundingBoxes(intersectionPoints);

    await Promise.all(newBoundingBoxes.map(box => box.computeMeshPointsDistances()));
    await Promise.all(newBoundingBoxes.map(box => box.computeFalsePositiveByRaycast()));

    boundingBoxes.push(...newBoundingBoxes);
}

/**
 * Affiche la carte des oclusions entre les dents.
 */
export const displayOclusionsMap = async () => {

    if (!boundingBoxes || boundingBoxes.length === 0) {
        await computeOclusionsMap();
    }

    const allToothObjects = threeEngine.getAllToothObjects();
    if (!allToothObjects) return;
    allToothObjects.forEach(tooth => {
        colorizeToothMeshVertices(tooth);
    });
}

/**
 * Affiche les stripping entre les dents.
 */
export const displayTeethStripping = async () => {

    if (!boundingBoxes || boundingBoxes.length === 0) {
        await computeOclusionsMap();
    }

    boundingBoxes.forEach(box => {
        if (box.contactA.body.teethRef.jawSide === box.contactB.body.teethRef.jawSide) {
            box.displayStripping();
        }
    });
}

/**
 * Met en transparent les dents voisines de la dent sélectionnée. ( elles sont en contact )
 * @param {Object} selectedTooth - La dent sélectionnée.
 */
export const displayNeiboringTeeth = async (selectedTooth) => {
    boundingBoxes.forEach(box => {
        if (box.contactA.body.teethRef === selectedTooth) {
            if (getViewportMode() === 'single' || selectedTooth.jawSide === box.contactB.body.teethRef.jawSide) {
                box.contactB.body.teethRef.refObj.material.transparent = true;
                box.contactB.body.teethRef.refObj.material.opacity = 0.65;
            }
        }
        else if (box.contactB.body.teethRef === selectedTooth) {
            if (getViewportMode() === 'single' || selectedTooth.jawSide === box.contactA.body.teethRef.jawSide) {
                box.contactA.body.teethRef.refObj.material.transparent = true;
                box.contactA.body.teethRef.refObj.material.opacity = 0.65;
            }
        }
    });
}


/**
 * Calcule et affiche l'intersection des dents.
 * 
 * Cette fonction commence par masquer les intersections de dents existantes.
 * Si une dent a été cliquée, elle met à jour l'intersection de cette dent.
 * Ensuite, si l'interférence est activée dans le store, elle affiche la carte des occlusions
 * et affiche les dents voisines de la dent cliquée.
 * Enfin, si le stripping est activé dans le store, elle affiche le stripping des dents.
 * 
 */
export const computeAndDisplayTeethIntersection = async () => {

    const allToothObjects = threeEngine.getAllToothObjects();
    if (!allToothObjects) return;

    unDisplayTeethIntersection();

    if (!Store.getState().CollisionReducer.isStripingEnabled
        && !Store.getState().CollisionReducer.isInterferenceEnabled) {
        allToothObjects.forEach(tooth => {
            synchronizeColliderTransformation(tooth.body, tooth.refObj);
        });
        return;
    }

    const selectedTooth = meshSelectionStore.getClickedTooth();
    if (selectedTooth) {
        await updateToothIntersection(selectedTooth);
    }

    if (Store.getState().CollisionReducer.isInterferenceEnabled) {
        await displayOclusionsMap();

        if (selectedTooth) {
            displayNeiboringTeeth(selectedTooth);
        }
    }

    if (Store.getState().CollisionReducer.isStripingEnabled) {
        await displayTeethStripping();
    }
}

/**
 * Applique une rotation à un point selon une quaternion.
 * @param {Object} point - Le point à faire pivoter.
 * @param {Object} rotation - La rotation à appliquer sous forme de quaternion.
 * @returns {Object} - Le point après application de la rotation.
 */
function applyRotation(point, rotation) {
    const quat = new Quaternion().set(rotation.x, rotation.y, rotation.z, rotation.w);
    const vec = new Vector3(point.x, point.y, point.z);
    return vec.applyQuaternion(quat);
}

/**
 * Réinitialise l'affichage des intersections des dents.
 * 
 * Cette fonction parcourt toutes les boîtes englobantes (boundingBoxes) et réinitialise
 * le matériau des objets de référence des dents (teethRef.refObj.material) pour chaque contact.
 * Elle vide également le groupe temporaire (tempGroup.children) et réinitialise la liste des
 * derniers contacts (lastContacts).
 */
export const unDisplayTeethIntersection = (forceWhite = false) => {

    const allToothObjects = threeEngine.getAllToothObjects();
    if (!allToothObjects) return;

    allToothObjects.forEach(tooth => {
        tooth.chooseAMaterial(forceWhite);
    });

    tempGroup.traverse((child) => {
        if (child.geometry) {
            child.geometry.dispose();
        }
        if (child.material) {
            if (Array.isArray(child.material)) {
                child.material.forEach(m => m.dispose());
            } else {
                child.material.dispose();
            }
        }
    });
    tempGroup.children = [];
    lastContacts = [];
}

export const clearBoundingBoxes = () => {
    boundingBoxes.forEach(box => {
        if (box.box) {
            box.box = null;
        }
    });
    boundingBoxes = [];
}
